import { APIReferenceType, ArdoqId, ViewIds } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { SettingsType, updateViewSettings } from '@ardoq/view-settings';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import { DependencyMatrixViewSettings } from './types';
import { dispatchAction } from '@ardoq/rxbeach';
import { localeCompare, type Locale } from '@ardoq/locale';
import { SHOW_ONLY_CONNECTED_COMPONENTS } from '@ardoq/global-consts';
import { t, type I18nLocale } from '@ardoq/i18n';

const getWorkspaceSelectLabel = (
  { referenceToWsId }: DependencyMatrixViewSettings,
  workspaceNamesByWorkspaceId: Record<ArdoqId, string>,
  i18nLocale: I18nLocale
) => {
  const workspaceName =
    referenceToWsId && workspaceNamesByWorkspaceId[referenceToWsId];
  const name =
    workspaceName ||
    t(
      i18nLocale,
      'tabview:dependencyMatrix.currentWorkspace',
      'Current workspace'
    );

  return t(
    i18nLocale,
    'tabview:dependencyMatrix.referenceTo',
    {
      name,
    },
    'Reference to: {{name}}'
  );
};

const getWorkspaceOptions = (
  viewId: ViewIds,
  viewState: DependencyMatrixViewSettings,
  workspaceIds: ArdoqId[],
  workspaceNamesByWorkspaceId: Record<ArdoqId, string>
) =>
  workspaceIds.map(workspaceId => {
    const label = workspaceNamesByWorkspaceId[workspaceId] ?? null;

    return {
      label,
      name: label,
      isActive: viewState.referenceToWsId === workspaceId,
      onClick: getWorkspaceOnClick(viewId, workspaceId),
      type: DropdownOptionType.OPTION,
    };
  });

const getWorkspaceOnClick = (viewId: ViewIds, id: ArdoqId) => () => {
  const referenceToWsId = id;
  handleStoreSetting(viewId, { referenceToWsId });
};

const getRefTypeOptionOnClick =
  (
    viewId: ViewIds,
    currentReferenceCreationType: Record<ArdoqId, number>,
    workspaceId: ArdoqId,
    refTypeIndex?: number
  ) =>
  () => {
    const referenceCreationType: Record<ArdoqId, number | undefined> = {
      ...currentReferenceCreationType,
    };

    referenceCreationType[workspaceId] = refTypeIndex;
    handleStoreSetting(viewId, { referenceCreationType });
  };

const getReferenceCreationOptions = (
  viewId: ViewIds,
  viewState: DependencyMatrixViewSettings,
  refTypes: APIReferenceType[],
  workspaceId: ArdoqId,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const noRefTypeSelectedLabel = t(
    i18nLocale,
    'tabview:dependencyMatrix.unspecified',
    'Unspecified'
  );
  const { referenceCreationType } = viewState;
  const currentReferenceCreationType = referenceCreationType[workspaceId];

  const refTypeOptions = Object.values(refTypes)
    .map(refType => ({
      label: refType.name,
      name: refType.name,
      isActive: currentReferenceCreationType === refType.id,
      onClick: getRefTypeOptionOnClick(
        viewId,
        referenceCreationType,
        workspaceId,
        refType.id
      ),
      type: DropdownOptionType.OPTION,
    }))
    .sort((a, b) => localeCompare(a.name, b.name, locale));

  return [
    {
      label: t(
        i18nLocale,
        'tabview:dependencyMatrix.referenceType',
        'Reference type'
      ),
      type: DropdownOptionType.HEADER,
    },
    {
      label: noRefTypeSelectedLabel,
      name: noRefTypeSelectedLabel,
      isActive: currentReferenceCreationType === undefined,
      onClick: getRefTypeOptionOnClick(
        viewId,
        referenceCreationType,
        workspaceId
      ),
      type: DropdownOptionType.OPTION,
    },
    {
      type: DropdownOptionType.DIVIDER,
    },
    ...refTypeOptions,
  ];
};

const getReferenceCreationSelectLabel = (
  viewState: DependencyMatrixViewSettings,
  refTypes: APIReferenceType[],
  workspaceId: ArdoqId,
  i18nLocale: I18nLocale
) => {
  const onCellClickPrefix = t(
    i18nLocale,
    'tabview:dependencyMatrix.onCellClick',
    'On cell click:'
  );
  const { referenceCreationType } = viewState;
  const currentReferenceCreationType = referenceCreationType[workspaceId];
  const selectedRefName = refTypes.find(
    ({ id }) => id === currentReferenceCreationType
  )?.name;

  if (selectedRefName) {
    return `${onCellClickPrefix} ${t(
      i18nLocale,
      'tabview:dependencyMatrix.createNamedReference',
      { name: selectedRefName },
      "Create '{{name}}' reference"
    )}`;
  }
  return `${onCellClickPrefix} ${t(
    i18nLocale,
    'tabview:dependencyMatrix.createReference',
    'Create reference'
  )}`;
};

type GetLeftMenuConfigArgs = {
  viewId: ViewIds;
  viewState: DependencyMatrixViewSettings;
  workspaceId: ArdoqId;
  workspaceIds: ArdoqId[];
  workspaceNamesByWorkspaceId: Record<ArdoqId, string>;
  referenceTypes: APIReferenceType[];
  locale: Locale;
  i18nLocale: I18nLocale;
};
export const getLeftMenuConfig = ({
  viewId,
  viewState,
  workspaceId,
  workspaceIds,
  workspaceNamesByWorkspaceId,
  referenceTypes,
  locale,
  i18nLocale,
}: GetLeftMenuConfigArgs) => {
  const { showOnlyConnectedComponents } = viewState;

  return [
    {
      id: 'selectReferenceToWs',
      label: getWorkspaceSelectLabel(
        viewState,
        workspaceNamesByWorkspaceId,
        i18nLocale
      ),
      iconName: IconName.WORKSPACE,
      options: getWorkspaceOptions(
        viewId,
        viewState,
        workspaceIds,
        workspaceNamesByWorkspaceId
      ),
      type: SettingsType.DROPDOWN,
    },
    {
      id: 'selectReferenceCreationType',
      options: getReferenceCreationOptions(
        viewId,
        viewState,
        referenceTypes,
        workspaceId,
        locale,
        i18nLocale
      ),
      label: getReferenceCreationSelectLabel(
        viewState,
        referenceTypes,
        workspaceId,
        i18nLocale
      ),
      iconName: IconName.REFERENCE,
      type: SettingsType.DROPDOWN,
    },
    {
      id: 'showOnlyConnectedComponents',
      label: SHOW_ONLY_CONNECTED_COMPONENTS,
      iconName: IconName.LINK,
      type: SettingsType.TOGGLE,
      isActive: showOnlyConnectedComponents,
      onClick: () => {
        dispatchAction(
          updateViewSettings({
            viewId,
            settings: {
              showOnlyConnectedComponents: !showOnlyConnectedComponents,
            },
            persistent: true,
          })
        );
      },
    },
  ];
};
